import { z } from "zod";
import { estimateTextMs } from "../../timeline/reading-rate";
import {
	blockProvenanceHintSchema,
	compact,
	emptyBlockSettingsSchema,
	joinParts,
	optionalStr,
	pickProvenance,
	str,
} from "../content-helpers";
import { ALL_BLOCK_PATCH_OPS, defineBlockKind } from "../define-block-kind";
import { richTextBlockDocNode } from "../doc-node";
import {
	presentationRichTextToPlainText,
	presentationRichTextV1Schema,
	readBlockPresentationRichText,
} from "../rich-text";

/** Plain text, preferring the editor's rich text when present. */
function richOrPlainText(content: Record<string, unknown>): string {
	const rich = readBlockPresentationRichText(content);
	if (rich) {
		const plain = presentationRichTextToPlainText(rich).trim();
		if (plain.length > 0) {
			return plain;
		}
	}
	return str(content.content);
}

function pickRichText(raw: Record<string, unknown>) {
	const parsed = presentationRichTextV1Schema.safeParse(raw.richText);
	return parsed.success ? { richText: parsed.data } : {};
}

export const textBlockContentSchema = z
	.object({
		content: z.string(),
		richText: presentationRichTextV1Schema.optional(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type TextBlockContent = z.infer<typeof textBlockContentSchema>;

const normalizeTextContent = (raw: Record<string, unknown>): TextBlockContent =>
	compact({
		content: str(raw.content),
		...pickRichText(raw),
		...pickProvenance(raw),
	});

export const textBlockKind = defineBlockKind({
	type: "TEXT",
	contentSchema: textBlockContentSchema,
	defaultContent: { content: "" },
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: ALL_BLOCK_PATCH_OPS,
	docNode: richTextBlockDocNode(normalizeTextContent),
	defaultSettings: {},
	partitionRole: "stackable",
	textWeight: 1,
	normalizeContent: normalizeTextContent,
	deriveNarrationText: richOrPlainText,
	estimateDwellMs: (content) => estimateTextMs(richOrPlainText(content)),
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "static" },
});

export const CALLOUT_VARIANTS = [
	"info",
	"tip",
	"warning",
	"important",
	"example",
	"quote",
] as const;

export type CalloutVariant = (typeof CALLOUT_VARIANTS)[number];

export const calloutBlockContentSchema = z
	.object({
		variant: z.enum(CALLOUT_VARIANTS),
		content: z.string(),
		title: z.string().optional(),
		/** `quote`: who said it. */
		attribution: z.string().optional(),
		source: z.string().optional(),
		caption: z.string().optional(),
		richText: presentationRichTextV1Schema.optional(),
		provenanceHint: blockProvenanceHintSchema.optional(),
	})
	.strict();

export type CalloutBlockContent = z.infer<typeof calloutBlockContentSchema>;

function normalizeCalloutVariant(value: unknown): CalloutVariant {
	return (CALLOUT_VARIANTS as readonly string[]).includes(String(value))
		? (value as CalloutVariant)
		: "info";
}

const normalizeCalloutContent = (
	raw: Record<string, unknown>,
): CalloutBlockContent =>
	compact({
		variant: normalizeCalloutVariant(raw.variant),
		content: str(raw.content),
		title: optionalStr(raw.title),
		attribution: optionalStr(raw.attribution),
		source: optionalStr(raw.source),
		caption: optionalStr(raw.caption),
		...pickRichText(raw),
		...pickProvenance(raw),
	});

export const calloutBlockKind = defineBlockKind({
	type: "CALLOUT",
	contentSchema: calloutBlockContentSchema,
	defaultContent: { variant: "info", content: "" },
	settingsSchema: emptyBlockSettingsSchema,
	patchOps: ALL_BLOCK_PATCH_OPS,
	docNode: richTextBlockDocNode(normalizeCalloutContent),
	defaultSettings: {},
	partitionRole: "stackable",
	// `example` / `quote` were their own full-card kinds before the P3 merge; keep that layout.
	resolvePartitionRole: (content) =>
		content.variant === "example" || content.variant === "quote"
			? "full_card"
			: "stackable",
	textWeight: 1,
	normalizeContent: normalizeCalloutContent,
	deriveNarrationText: (content) => {
		const body = richOrPlainText(content);
		const attribution = str(content.attribution);
		return content.variant === "quote" && attribution
			? `${body}\n\n${attribution}`
			: body;
	},
	estimateDwellMs: (content) =>
		estimateTextMs(
			joinParts([
				str(content.title),
				richOrPlainText(content),
				str(content.attribution),
				str(content.caption),
			]),
		),
	gate: () => [],
	interactions: [],
	exportPolicy: { pdf: "static" },
});
